'use client';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '@/app/store';
import { selectCurrentUser } from '@/app/store/slices/userSlice';
import {
    uploadProfilePictureThunk,
    selectDefaultAvatarThunk,
    deleteProfilePictureThunk,
} from '@/app/store/thunks/userThunks';
import { listDefaultAvatars, DefaultAvatarDto } from '@/app/services/api/profilePictureService';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import Avatar from '@/app/components/Avatar';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface ProfilePictureModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const AvatarGridItem = memo(function AvatarGridItem({
    avatar, isSelected, disabled, onSelect,
}: {
    avatar: DefaultAvatarDto;
    isSelected: boolean;
    disabled: boolean;
    onSelect: (id: number) => void;
}) {
    const [loaded, setLoaded] = useState(false);

    return (
        <button
            type="button"
            title={avatar.name}
            disabled={disabled}
            onClick={() => onSelect(avatar.id)}
            className={`relative rounded-xl overflow-hidden border-2 p-0.5 transition-all duration-150 ${
                isSelected
                    ? 'border-primary ring-2 ring-primary/30 scale-110 shadow-md'
                    : 'border-transparent hover:border-muted-foreground/40 hover:shadow-sm'
            }`}
        >
            {!loaded && (
                <div className="w-10 h-10 rounded-lg bg-muted animate-pulse" />
            )}
            <img
                src={avatar.url}
                alt={avatar.name}
                className={`w-10 h-10 rounded-lg object-contain transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                onLoad={() => setLoaded(true)}
            />
            {isSelected && (
                <div className="absolute inset-0 rounded-xl bg-primary/10" />
            )}
        </button>
    );
});
AvatarGridItem.displayName = 'AvatarGridItem';

const ProfilePictureModal = memo(function ProfilePictureModal({ isOpen, onClose }: ProfilePictureModalProps) {
    const dispatch = useDispatch<AppDispatch>();
    const currentUser = useSelector(selectCurrentUser);
    const { t } = useTranslation('profilePicture');
    const { t: tCommon } = useTranslation('common');

    const [defaultAvatars, setDefaultAvatars] = useState<DefaultAvatarDto[]>([]);
    const [selectedAvatarId, setSelectedAvatarId] = useState<number | null>(null);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingDefaults, setLoadingDefaults] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load default avatars when modal opens
    useEffect(() => {
        if (!isOpen) return;
        setLoadingDefaults(true);
        listDefaultAvatars()
            .then(setDefaultAvatars)
            .catch(() => setDefaultAvatars([]))
            .finally(() => setLoadingDefaults(false));
    }, [isOpen]);

    // Cleanup preview URL on unmount / file change
    useEffect(() => {
        return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    }, [previewUrl]);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!ALLOWED_TYPES.includes(file.type)) {
            setError(t('invalidFileType'));
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            setError(t('fileTooLarge'));
            return;
        }

        setError(null);
        setSelectedAvatarId(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    }, [previewUrl, t]);

    const handleSelectDefault = useCallback((id: number) => {
        setSelectedAvatarId(id);
        setPreviewFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setError(null);
    }, [previewUrl]);

    const loginName = currentUser?.userName ?? '';

    const handleConfirm = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            if (previewFile) {
                await dispatch(uploadProfilePictureThunk(previewFile, loginName));
            } else if (selectedAvatarId !== null) {
                await dispatch(selectDefaultAvatarThunk(selectedAvatarId, loginName));
            }
            setPreviewFile(null);
            setPreviewUrl(null);
            setSelectedAvatarId(null);
            onClose();
        } catch (err: any) {
            setError(err?.message ?? t('genericError'));
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, previewFile, selectedAvatarId, loginName, onClose, t]);

    const handleRemove = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            await dispatch(deleteProfilePictureThunk(loginName));
            onClose();
        } catch (err: any) {
            setError(err?.message ?? t('genericError'));
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, loginName, onClose, t]);

    const handleClose = useCallback(() => {
        if (isLoading) return;
        setPreviewFile(null);
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setSelectedAvatarId(null);
        setError(null);
        onClose();
    }, [isLoading, previewUrl, onClose]);

    const hasSelection = previewFile !== null || selectedAvatarId !== null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => { if (!isLoading && !open) handleClose(); }}>
            <DialogContent className="w-[calc(100%-2rem)] sm:w-full max-w-md max-h-[85vh] !p-0 gap-0 flex flex-col bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-border">
                    <DialogTitle>{t('modalTitle')}</DialogTitle>
                </DialogHeader>

                {/* Scrollable middle — grows to fill space, overflows internally */}
                <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                    <div className="flex flex-col gap-4">
                        {/* Current / Preview avatar */}
                        <div className="flex flex-col items-center gap-2">
                            <Avatar
                                profilePictureUrl={previewUrl ?? currentUser?.profilePictureUrl}
                                displayName={currentUser?.displayName}
                                givenName={currentUser?.givenName ?? undefined}
                                surname={currentUser?.surname ?? undefined}
                                size="lg"
                            />
                            {previewFile && (
                                <span className="text-xs text-muted-foreground">{t('previewHint')}</span>
                            )}
                        </div>

                        {/* Upload section */}
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium">{t('uploadButton')}</span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                onChange={handleFileChange}
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={() => fileInputRef.current?.click()}
                                className="self-start px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
                            >
                                {t('chooseFile')}
                            </button>
                            {previewFile && (
                                <span className="text-xs text-muted-foreground truncate">{previewFile.name}</span>
                            )}
                        </div>

                        {/* Default avatar grid */}
                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium">{t('selectDefault')}</span>
                            {loadingDefaults ? (
                                <div className="flex justify-center py-4">
                                    <div className="w-5 h-5 border-2 border-border border-t-primary rounded-full animate-spin" />
                                </div>
                            ) : defaultAvatars.length === 0 ? (
                                <span className="text-xs text-muted-foreground">{t('noDefaultAvatars')}</span>
                            ) : (
                                <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
                                    {defaultAvatars.map((avatar) => (
                                        <AvatarGridItem
                                            key={avatar.id}
                                            avatar={avatar}
                                            isSelected={selectedAvatarId === avatar.id}
                                            disabled={isLoading}
                                            onSelect={handleSelectDefault}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Error */}
                        {error && <p className="text-sm text-destructive">{error}</p>}
                    </div>
                </div>

                {/* Sticky footer — always visible */}
                <div className="flex items-center justify-between gap-2 px-6 py-4 border-t border-border shrink-0">
                    <button
                        type="button"
                        disabled={isLoading || !currentUser?.profilePictureUrl}
                        onClick={handleRemove}
                        className="text-sm text-destructive hover:underline disabled:opacity-40 disabled:no-underline"
                    >
                        {isLoading ? t('removing') : t('removeButton')}
                    </button>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            disabled={isLoading}
                            onClick={handleClose}
                            className="px-4 py-1.5 text-sm border border-border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
                        >
                            {tCommon('cancel')}
                        </button>
                        <button
                            type="button"
                            disabled={isLoading || !hasSelection}
                            onClick={handleConfirm}
                            // Softer primary at rest, deepening on hover —
                            // less attention-grabbing than the solid header
                            // purple but still clearly the affirmative
                            // action. Disabled state uses an explicit muted
                            // palette rather than opacity-50 (the opacity
                            // overlay washed the white label into the
                            // background and the text became hard to read).
                            className="px-4 py-1.5 text-sm rounded-md transition-colors bg-primary/80 text-primary-foreground hover:bg-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <span className="flex items-center gap-1.5">
                                    <span className="w-3 h-3 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                                    {t('saving')}
                                </span>
                            ) : tCommon('confirm')}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
});

ProfilePictureModal.displayName = 'ProfilePictureModal';

export default ProfilePictureModal;
